/**
 * PendingDeletionsSection - Admin section for managing accounts pending deletion
 *
 * Shows users who have requested account deletion with options to:
 * - Reactivate account (cancel deletion)
 * - Permanently delete (only after grace period has passed)
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui';
import { adminService } from '../../services/admin.service';
import type { UserPendingDeletion } from '@budgira/shared';

export function PendingDeletionsSection() {
  const { t, i18n } = useTranslation();
  const [users, setUsers] = useState<UserPendingDeletion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmPermanentDelete, setConfirmPermanentDelete] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await adminService.getUsersPendingDeletion();
      setUsers(data);
    } catch {
      setError(t('admin.pendingDeletions.loadError', 'Erreur lors du chargement'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleReactivate = async (userId: string) => {
    setActionLoading(userId);
    try {
      await adminService.reactivateUser(userId);
      await loadUsers();
    } catch {
      setError(t('admin.pendingDeletions.reactivateError', 'Erreur lors de la réactivation'));
    } finally {
      setActionLoading(null);
    }
  };

  const handlePermanentDelete = async (userId: string) => {
    setActionLoading(userId);
    try {
      await adminService.permanentlyDeleteUser(userId);
      setConfirmPermanentDelete(null);
      await loadUsers();
    } catch {
      setError(t('admin.pendingDeletions.deleteError', 'Erreur lors de la suppression'));
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDaysRemaining = (scheduledDate: string) => {
    const now = new Date();
    const scheduled = new Date(scheduledDate);
    const diffTime = scheduled.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  if (isLoading) {
    return (
      <Card>
        <div className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600"></div>
        </div>
      </Card>
    );
  }

  if (users.length === 0) {
    return null; // Don't show section if no pending deletions
  }

  return (
    <Card className="border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-900/20">
      <div className="p-4 border-b border-orange-200 dark:border-orange-800">
        <div className="flex items-center gap-2">
          <ClockIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
          <h3 className="font-semibold text-orange-900 dark:text-orange-200">
            {t('admin.pendingDeletions.title', 'Comptes en cours de suppression')}
          </h3>
          <span className="px-2 py-0.5 text-xs font-medium bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200 rounded-full">
            {users.length}
          </span>
        </div>
        <p className="mt-1 text-sm text-orange-700 dark:text-orange-300">
          {t('admin.pendingDeletions.description', 'Ces comptes ont demandé leur suppression. Vous pouvez les réactiver ou les supprimer définitivement après le délai.')}
        </p>
      </div>

      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            {t('common.close', 'Fermer')}
          </button>
        </div>
      )}

      <div className="divide-y divide-orange-200 dark:divide-orange-800">
        {users.map((user) => {
          const daysRemaining = getDaysRemaining(user.deletionScheduledAt);
          const canDelete = user.canPermanentlyDelete;

          return (
            <div key={user.id} className="p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                {/* User info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 dark:text-slate-100 truncate">
                    {user.email}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
                    <span>
                      {t('admin.pendingDeletions.requestedOn', 'Demandé le')} {formatDate(user.deletionRequestedAt)}
                    </span>
                    {canDelete ? (
                      <span className="text-red-600 dark:text-red-400 font-medium">
                        {t('admin.pendingDeletions.readyToDelete', 'Prêt pour suppression définitive')}
                      </span>
                    ) : (
                      <span>
                        {t('admin.pendingDeletions.daysRemaining', '{{count}} jour(s) restant(s)', { count: daysRemaining })}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Reactivate button */}
                  <button
                    onClick={() => handleReactivate(user.id)}
                    disabled={actionLoading === user.id}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/40 hover:bg-green-100 dark:hover:bg-green-900/60 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {actionLoading === user.id ? (
                      <LoadingIcon className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshIcon className="w-4 h-4" />
                    )}
                    {t('admin.pendingDeletions.reactivate', 'Réactiver')}
                  </button>

                  {/* Permanent delete button */}
                  {confirmPermanentDelete === user.id ? (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePermanentDelete(user.id)}
                        disabled={actionLoading === user.id}
                        className="px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {actionLoading === user.id ? '...' : t('common.confirm', 'Confirmer')}
                      </button>
                      <button
                        onClick={() => setConfirmPermanentDelete(null)}
                        className="px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                      >
                        {t('common.cancel', 'Annuler')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmPermanentDelete(user.id)}
                      disabled={!canDelete || actionLoading === user.id}
                      className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
                        canDelete
                          ? 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/40 hover:bg-red-100 dark:hover:bg-red-900/60'
                          : 'text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-800 cursor-not-allowed'
                      }`}
                      title={!canDelete ? t('admin.pendingDeletions.waitForDelay', 'Attendre la fin du délai') : ''}
                    >
                      <TrashIcon className="w-4 h-4" />
                      {t('admin.pendingDeletions.permanentDelete', 'Supprimer définitivement')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// Icons
function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  );
}

function LoadingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );
}

export default PendingDeletionsSection;
